import { FilePlusIcon } from '@radix-ui/react-icons'
import { useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

interface BatchSearchUploadStepProps {
  onFileUpload: (file: File) => void
  loading: boolean
  uploadedFile?: File | null
}

export const BatchSearchUploadStep = ({
  onFileUpload,
  loading,
}: BatchSearchUploadStepProps) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      onFileUpload(acceptedFiles[0])
    }
  }

  const onDropRejected = (e: any) => {
    console.error('File rejected:', e)
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        '.xlsx',
      ],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    noClick: true,
    disabled: loading,
  })

  const onPressLoadFile = () => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  return (
    <div className="flex-1 flex flex-col justify-center items-center gap-6 w-full h-full">
      <Card
        className={`border-0 flex-1 flex flex-col justify-between lg:p-5 w-full h-full`}
      >
        <div
          {...getRootProps()}
          className="flex flex-col m-2 lg:m-6 border-[3px] border-primaryColor30 border-dashed rounded-lg flex-1 justify-center items-center p-6"
        >
          <div className="flex flex-col justify-center items-center flex-1">
            <input {...getInputProps()} ref={inputRef} />

            <div className="mb-8">
              {loading ? (
                <div className="flex justify-center items-center w-full h-full">
                  <LottieSpinner
                    width={50}
                    height={50}
                    text={t('dashboard.loading.loading')}
                  />
                </div>
              ) : (
                <FilePlusIcon
                  className="w-20 h-20"
                  color={weCheckPalette.dynamicBlue}
                />
              )}
            </div>

            <h1 className="text-3xl font-medium text-neutralColor mb-2">
              {t('dashboard.sidebar.batchSearch')}
            </h1>

            {!loading && (
              <div className="flex flex-col gap-4 justify-center items-center">
                <div className="flex flex-col lg:flex-row items-center gap-2">
                  <p className="text-neutralColor60 text-base font-normal tracking-wider">
                    {t(
                      'dashboard.batchSearch.addFile',
                      'Add file, supported formats:',
                    )}
                  </p>
                  <div className="flex flex-row gap-2">
                    <span className="rounded-full bg-alertBlueBase px-2 py-1 text-xs text-alertBlueSecondary">
                      XLSX
                    </span>
                    <span className="rounded-full bg-alertGreenBase px-2 py-1 text-xs text-alertGreenSecondary">
                      XLS
                    </span>
                    <span className="rounded-full bg-alertYellowBase px-2 py-1 text-xs text-alertYellowSecondary">
                      CSV
                    </span>
                  </div>
                </div>

                <div className="flex flex-row gap-3">
                  <Button
                    onClick={onPressLoadFile}
                    isLoading={loading}
                    disabled={loading}
                  >
                    {t('dashboard.batchSearch.chooseFile', 'Choose File')}
                  </Button>
                </div>

                <p className="text-neutralColor60 text-base">
                  {t('dashboard.facialMatching.orDropFiles')}
                </p>
              </div>
            )}

            {loading && (
              <p className="text-neutralColor60 text-center mt-4">
                {t(
                  'dashboard.batchSearch.validatingFile',
                  'Validating file...',
                )}
              </p>
            )}
          </div>

          {!loading && (
            <div className="text-center lg:flex lg:flex-row lg:justify-center lg:w-full">
              <p className="text-neutralColor text-xs lg:ml-1">
                <span
                  className="text-linkColor cursor-pointer"
                  role="button"
                  onClick={() =>
                    window.open(
                      t('dashboard.common.termsAndConditionsLink'),
                      '_blank',
                    )
                  }
                  tabIndex={0}
                >
                  {t('dashboard.facialMatching.termsAndConditions')}
                </span>
                &nbsp;
                {t('dashboard.batchSearch.apply1Credit')}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Template Download */}
      <div className="flex flex-col items-center gap-4">
        <p className="text-neutralColor60 text-sm text-center">
          {t(
            'dashboard.batchSearch.needTemplate',
            'Need a template to get started?',
          )}
        </p>
        <a href="/Batch_Search_Template.xlsx" download className="inline-block">
          <Button type="button" variant="outline">
            {t('dashboard.batchSearch.downloadTemplate', 'Download Template')}
          </Button>
        </a>
        <p className="text-neutralColor60 max-w-md text-center text-xs">
          {t('dashboard.batchSearch.focusedTopicsHint')}
        </p>
      </div>
    </div>
  )
}
